/**
 * Shared structured condition builder (§B-3/§B-4) — the recursive @cairn/shared
 * Condition DSL edited as "match ALL/ANY of leaf rows" (field·op·value, NOT per
 * leaf) with a raw-JSON advanced escape hatch. Catalog-parameterized so routing
 * (ROUTING_FIELDS) and readiness (READINESS_FIELDS) reuse one implementation.
 */
import {
  opsForType,
  opLabel,
  fieldDefIn,
  emptyLeaf,
  builderToCondition,
  conditionToBuilder,
  type FieldDef,
  type BuilderState,
  type LeafState,
} from "../../lib/conditionModel.js";
import type { ConditionOperator, Condition } from "@cairn/shared";
import { Select, Icon, Button } from "../../components/ui/index.js";

function safeParse(s: string): Condition {
  try {
    return JSON.parse(s) as Condition;
  } catch {
    return { all: [] };
  }
}

export function ConditionBuilder({
  fields,
  builder,
  onChange,
}: {
  fields: FieldDef[];
  builder: BuilderState;
  onChange: (next: BuilderState) => void;
}) {
  const patchLeaf = (i: number, patch: Partial<LeafState>) =>
    onChange({ ...builder, leaves: builder.leaves.map((l, j) => (j === i ? { ...l, ...patch } : l)) });
  const addLeaf = () => onChange({ ...builder, leaves: [...builder.leaves, emptyLeaf(fields)] });
  const removeLeaf = (i: number) => onChange({ ...builder, leaves: builder.leaves.filter((_, j) => j !== i) });

  return (
    <div className="cond">
      <div className="cond__head">
        <span className="cond__title">Condition</span>
        <button
          type="button"
          className="cond__mode"
          onClick={() =>
            onChange(
              builder.advanced
                ? conditionToBuilder(safeParse(builder.raw))
                : { ...builder, advanced: true, raw: JSON.stringify(builderToCondition(builder, fields), null, 2) },
            )
          }
        >
          {builder.advanced ? "Use builder" : "Edit as JSON"}
        </button>
      </div>

      {builder.advanced ? (
        <textarea
          className="cond__json control"
          rows={8}
          value={builder.raw}
          onChange={(e) => onChange({ ...builder, raw: e.target.value })}
          aria-label="Condition JSON"
          spellCheck={false}
        />
      ) : (
        <>
          <div className="cond__match">
            Match
            <Select
              ariaLabel="Match all or any"
              value={builder.match}
              onChange={(v) => onChange({ ...builder, match: v as "all" | "any" })}
              options={[
                { value: "all", label: "ALL of" },
                { value: "any", label: "ANY of" },
              ]}
              className="cond__match-select"
            />
            these conditions
          </div>

          {builder.leaves.length === 0 && (
            <p className="cond__empty muted">No conditions — matches every order. Add one, or leave empty for a catch-all.</p>
          )}

          <ul className="cond__leaves">
            {builder.leaves.map((leaf, i) => (
              <LeafRow key={i} fields={fields} leaf={leaf} onChange={(p) => patchLeaf(i, p)} onRemove={() => removeLeaf(i)} />
            ))}
          </ul>

          <Button variant="ghost" size="sm" icon="plus" onClick={addLeaf}>
            Add condition
          </Button>
        </>
      )}
    </div>
  );
}

function LeafRow({
  fields,
  leaf,
  onChange,
  onRemove,
}: {
  fields: FieldDef[];
  leaf: LeafState;
  onChange: (patch: Partial<LeafState>) => void;
  onRemove: () => void;
}) {
  const def = fieldDefIn(fields, leaf.field);
  const type = def?.type ?? "string";
  const ops = opsForType(type);
  const op = ops.includes(leaf.op) ? leaf.op : ops[0]!;
  const isArrayOp = op === "in" || op === "nin";

  return (
    <li className="leaf">
      <label className="leaf__neg" title="Negate this condition">
        <input type="checkbox" checked={leaf.negate} onChange={(e) => onChange({ negate: e.target.checked })} />
        <span>not</span>
      </label>
      <Select
        ariaLabel="Field"
        value={leaf.field}
        onChange={(v) => {
          const nextOps = opsForType(fieldDefIn(fields, v)?.type ?? "string");
          onChange({ field: v, op: nextOps.includes(leaf.op) ? leaf.op : nextOps[0]! });
        }}
        options={fields.map((f) => ({ value: f.field, label: f.label }))}
        className="leaf__field"
      />
      <Select
        ariaLabel="Operator"
        value={op}
        onChange={(v) => onChange({ op: v as ConditionOperator })}
        options={ops.map((o) => ({ value: o, label: opLabel(o) }))}
        className="leaf__op"
      />
      <LeafValue leaf={{ ...leaf, op }} type={type} isArrayOp={isArrayOp} options={def?.options} onChange={onChange} />
      <button type="button" className="leaf__rm" aria-label="Remove condition" onClick={onRemove}>
        <Icon name="x" size={14} />
      </button>
    </li>
  );
}

function LeafValue({
  leaf,
  type,
  isArrayOp,
  options,
  onChange,
}: {
  leaf: LeafState;
  type: string;
  isArrayOp: boolean;
  options?: string[];
  onChange: (patch: Partial<LeafState>) => void;
}) {
  if (leaf.op === "exists") return <span className="leaf__novalue muted">— no value —</span>;
  if (type === "boolean") {
    return (
      <Select
        ariaLabel="Value"
        value={leaf.value || "true"}
        onChange={(v) => onChange({ value: v })}
        options={[
          { value: "true", label: "true" },
          { value: "false", label: "false" },
        ]}
        className="leaf__val"
      />
    );
  }
  if (type === "enum" && options && !isArrayOp) {
    return (
      <Select
        ariaLabel="Value"
        value={leaf.value || options[0]!}
        onChange={(v) => onChange({ value: v })}
        options={options.map((o) => ({ value: o, label: o }))}
        className="leaf__val"
      />
    );
  }
  return (
    <input
      className="control leaf__val"
      aria-label="Value"
      value={leaf.value}
      onChange={(e) => onChange({ value: e.target.value })}
      placeholder={isArrayOp ? "comma, separated" : type === "number" ? "number" : "value"}
      inputMode={type === "number" && !isArrayOp ? "numeric" : undefined}
    />
  );
}
